import { useState, useEffect } from 'react'
import Users from '@/components/userManagement/Users'
import Roles from '@/components/userManagement/Roles'
import { WidgetAccess } from '@/components/userManagement/WidgetAccess'
import { Search, X, Filter } from 'lucide-react'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Role, User } from '@/redux/service/userManagement'
import { ColumnVisibilityPopover } from '@/components/ColumnVisibilityPopover'
import {
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core'
import { arrayMove, sortableKeyboardCoordinates } from '@dnd-kit/sortable'

const DEFAULT_USER_COLUMNS = [
  { key: 'user', label: 'User' },
  { key: 'email', label: 'Email' },
  { key: 'role', label: 'Role' },
  { key: 'team', label: 'Team' },
  { key: 'status', label: 'Status' },
]

const DEFAULT_ROLE_COLUMNS = [
  { key: 'role_name', label: 'Role Name' },
  { key: 'attio', label: 'Attio' },
  { key: 'clients', label: 'Clients' },
  { key: 'configurations', label: 'Config' },
  { key: 'datascope', label: 'Datascope' },
  { key: 'projects', label: 'Projects' },
  { key: 'timesheet', label: 'Timesheet' },
  { key: 'employees', label: 'Employees' },
  { key: 'finance', label: 'Finance' },
  { key: 'mini_finance', label: 'Project Finance' },
  { key: 'weekly_planning', label: 'Weekly Planning' },
  { key: 'admin_notes', label: 'Admin Notes' },
  { key: 'widgets', label: 'Widgets' },
  { key: 'created_date', label: 'Created Date' },
]

const UserManagement = () => {
  const [activeTab, setActiveTab] = useState<'users' | 'roles' | 'widgets'>(
    'users'
  )

  // Users filter states
  const [userSearch, setUserSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState<
    'active' | 'inactive' | 'all'
  >('active')
  const [isInviteModalOpen, setIsInviteModalOpen] = useState(false)
  const [selectedUser, setSelectedUser] = useState<User | null>(null)
  const [userSortBy, setUserSortBy] = useState<string | undefined>()
  const [userOrder, setUserOrder] = useState<'ASC' | 'DESC' | undefined>()
  const [userPage, setUserPage] = useState(1)

  // Roles filter states
  const [roleSearch, setRoleSearch] = useState('')
  const [isUpsertModalOpen, setIsUpsertModalOpen] = useState(false)
  const [selectedRole, setSelectedRole] = useState<Role | null>(null)

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  )

  // Users column state
  const [userColumns, setUserColumns] = useState<
    { key: string; label: string }[]
  >(() => {
    const saved = localStorage.getItem('user-table-column-order')
    if (saved) {
      try {
        const parsed = JSON.parse(saved)
        return parsed
          .map((key: string) => DEFAULT_USER_COLUMNS.find(c => c.key === key))
          .filter(Boolean) as { key: string; label: string }[]
      } catch {
        /* fallback */
      }
    }
    return DEFAULT_USER_COLUMNS
  })

  const [selectedUserColumns, setSelectedUserColumns] = useState<string[]>(
    () => {
      const saved = localStorage.getItem('user-table-columns')
      if (saved) {
        try {
          return JSON.parse(saved)
        } catch {
          /* fallback */
        }
      }
      return DEFAULT_USER_COLUMNS.map(col => col.key)
    }
  )

  // Roles column state
  const [roleColumns, setRoleColumns] = useState<
    { key: string; label: string }[]
  >(() => {
    const saved = localStorage.getItem('role-table-column-order')
    if (saved) {
      try {
        const parsed = JSON.parse(saved)
        const restored = parsed
          .map((key: string) => DEFAULT_ROLE_COLUMNS.find(c => c.key === key))
          .filter(Boolean) as { key: string; label: string }[]
        // Append any default columns not in the saved order (e.g. a
        // permission added after the user last customized this table) so
        // they show up without requiring a manual "Reset".
        const restoredKeys = new Set(restored.map(c => c.key))
        const newDefaults = DEFAULT_ROLE_COLUMNS.filter(
          c => !restoredKeys.has(c.key)
        )
        return [...restored, ...newDefaults]
      } catch {
        /* fallback */
      }
    }
    return DEFAULT_ROLE_COLUMNS
  })

  const [selectedRoleColumns, setSelectedRoleColumns] = useState<string[]>(
    () => {
      const saved = localStorage.getItem('role-table-columns')
      if (saved) {
        try {
          const parsed: string[] = JSON.parse(saved)
          const newDefaults = DEFAULT_ROLE_COLUMNS.map(c => c.key).filter(
            key => !parsed.includes(key)
          )
          return [...parsed, ...newDefaults]
        } catch {
          /* fallback */
        }
      }
      return DEFAULT_ROLE_COLUMNS.map(col => col.key)
    }
  )

  useEffect(() => {
    localStorage.setItem(
      'user-table-column-order',
      JSON.stringify(userColumns.map(c => c.key))
    )
    localStorage.setItem(
      'user-table-columns',
      JSON.stringify(selectedUserColumns)
    )
  }, [userColumns, selectedUserColumns])

  useEffect(() => {
    localStorage.setItem(
      'role-table-column-order',
      JSON.stringify(roleColumns.map(c => c.key))
    )
    localStorage.setItem(
      'role-table-columns',
      JSON.stringify(selectedRoleColumns)
    )
  }, [roleColumns, selectedRoleColumns])

  const handleUserDragEnd = (event: DragEndEvent) => {
    const { active, over } = event
    if (over && active.id !== over.id) {
      setUserColumns(items => {
        const oldIndex = items.findIndex(i => i.key === active.id)
        const newIndex = items.findIndex(i => i.key === over.id)
        return arrayMove(items, oldIndex, newIndex)
      })
    }
  }

  const handleRoleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event
    if (over && active.id !== over.id) {
      setRoleColumns(items => {
        const oldIndex = items.findIndex(i => i.key === active.id)
        const newIndex = items.findIndex(i => i.key === over.id)
        return arrayMove(items, oldIndex, newIndex)
      })
    }
  }

  const toggleUserColumn = (key: string) => {
    setSelectedUserColumns(prev =>
      prev.includes(key)
        ? prev.filter(c => c !== key && selectedUserColumns.length > 1)
        : [...prev, key]
    )
  }

  const toggleRoleColumn = (key: string) => {
    setSelectedRoleColumns(prev =>
      prev.includes(key)
        ? prev.filter(c => c !== key && selectedRoleColumns.length > 1)
        : [...prev, key]
    )
  }

  const isUserColumnVisible = (key: string) => selectedUserColumns.includes(key)
  const isRoleColumnVisible = (key: string) => selectedRoleColumns.includes(key)

  const handleClearUserFilters = () => {
    setUserSearch('')
    setUserSortBy(undefined)
    setUserOrder(undefined)
    setStatusFilter('active')
    setUserPage(1)
  }

  const handleClearRoleFilters = () => {
    setRoleSearch('')
  }

  return (
    <div className='h-full flex flex-col space-y-4 bg-background text-foreground overflow-hidden p-2'>
      <div className='shrink-0 flex flex-col lg:flex-row lg:items-center justify-between border-b border-border pb-3 mb-4 gap-4'>
        <div className='flex p-1 bg-muted border border-border rounded-xl gap-1 shrink-0 w-fit h-9 items-stretch'>
          {(['users', 'roles', 'widgets'] as const).map(tab => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-4 text-xs font-medium transition-all duration-200 rounded-lg flex items-center justify-center ${
                activeTab === tab
                  ? 'bg-primary text-primary-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground hover:bg-white/5'
              }`}
            >
              {tab.charAt(0).toUpperCase() + tab.slice(1)}
            </button>
          ))}
        </div>

        <div className='flex flex-col sm:flex-row items-center gap-3 w-full lg:w-auto'>
          {activeTab !== 'widgets' &&
            (activeTab === 'users' ? (
              <>
                <div className='relative w-full sm:w-64 shrink-0'>
                  <Search className='absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500' />
                  <Input
                    placeholder='Search users...'
                    value={userSearch}
                    onChange={e => {
                      setUserSearch(e.target.value)
                      setUserPage(1)
                    }}
                    className='pl-10 h-9 border-border text-foreground focus:border-primary transition-all rounded-lg text-xs bg-card'
                  />
                </div>
                <Select
                  value={statusFilter}
                  onValueChange={(value: 'active' | 'inactive' | 'all') => {
                    setStatusFilter(value)
                    setUserPage(1)
                  }}
                >
                  <SelectTrigger className='w-full sm:w-[140px] h-9 border-border text-foreground rounded-lg text-xs bg-card'>
                    <div className='flex items-center gap-1.5'>
                      <Filter className='w-3.5 h-3.5 text-slate-500' />
                      <SelectValue placeholder='Status' />
                    </div>
                  </SelectTrigger>
                  <SelectContent className='bg-popover border-border text-xs'>
                    <SelectItem value='active'>Active</SelectItem>
                    <SelectItem value='inactive'>Inactive</SelectItem>
                    <SelectItem value='all'>All Status</SelectItem>
                  </SelectContent>
                </Select>
                {(userSearch !== '' ||
                  userSortBy !== undefined ||
                  userOrder !== undefined ||
                  statusFilter !== 'active') && (
                  <Button
                    variant='ghost'
                    size='sm'
                    onClick={handleClearUserFilters}
                    className='h-9 px-3 text-xs text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors flex items-center gap-1.5'
                  >
                    <X className='w-3.5 h-3.5' /> Clear All
                  </Button>
                )}
                <Button
                  onClick={() => {
                    setSelectedUser(null)
                    setIsInviteModalOpen(true)
                  }}
                  className='bg-primary text-primary-foreground hover:bg-primary/90 h-9 px-4 rounded-lg text-xs font-medium shadow-sm w-full sm:w-auto'
                >
                  Invite User
                </Button>
              </>
            ) : (
              <>
                <div className='relative w-full sm:w-64 shrink-0'>
                  <Search className='absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500' />
                  <Input
                    placeholder='Search by role name...'
                    value={roleSearch}
                    onChange={e => {
                      setRoleSearch(e.target.value)
                    }}
                    className='pl-10 h-9 border-border text-foreground focus:border-primary transition-all rounded-lg text-xs bg-card'
                  />
                </div>
                {roleSearch !== '' && (
                  <Button
                    variant='ghost'
                    size='sm'
                    onClick={handleClearRoleFilters}
                    className='h-9 px-3 text-xs text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors flex items-center gap-1.5'
                  >
                    <X className='w-3.5 h-3.5' /> Clear All
                  </Button>
                )}
                <Button
                  onClick={() => {
                    setSelectedRole(null)
                    setIsUpsertModalOpen(true)
                  }}
                  className='bg-primary text-primary-foreground hover:bg-primary/90 h-9 px-4 rounded-lg text-xs font-medium shadow-sm w-full sm:w-auto'
                >
                  Add Role
                </Button>
              </>
            ))}

          {activeTab !== 'widgets' && (
            <ColumnVisibilityPopover
              columns={activeTab === 'users' ? userColumns : roleColumns}
              isColumnVisible={
                activeTab === 'users'
                  ? isUserColumnVisible
                  : isRoleColumnVisible
              }
              onToggle={
                activeTab === 'users' ? toggleUserColumn : toggleRoleColumn
              }
              onReset={() => {
                if (activeTab === 'users') {
                  setUserColumns(DEFAULT_USER_COLUMNS)
                  setSelectedUserColumns(DEFAULT_USER_COLUMNS.map(c => c.key))
                } else {
                  setRoleColumns(DEFAULT_ROLE_COLUMNS)
                  setSelectedRoleColumns(DEFAULT_ROLE_COLUMNS.map(c => c.key))
                }
              }}
              onDragEnd={
                activeTab === 'users' ? handleUserDragEnd : handleRoleDragEnd
              }
              sensors={sensors}
            />
          )}
        </div>
      </div>

      <div className='flex-1 overflow-hidden min-h-0'>
        {activeTab === 'users' && (
          <Users
            searchTerm={userSearch}
            setSearchTerm={setUserSearch}
            page={userPage}
            setPage={setUserPage}
            sortBy={userSortBy}
            setSortBy={setUserSortBy}
            order={userOrder}
            setOrder={setUserOrder}
            statusFilter={statusFilter}
            setStatusFilter={setStatusFilter}
            isInviteModalOpen={isInviteModalOpen}
            setIsInviteModalOpen={setIsInviteModalOpen}
            selectedUser={selectedUser}
            setSelectedUser={setSelectedUser}
            handleClearFilters={handleClearUserFilters}
            columns={userColumns}
            isColumnVisible={isUserColumnVisible}
          />
        )}

        {activeTab === 'roles' && (
          <Roles
            searchTerm={roleSearch}
            setSearchTerm={setRoleSearch}
            isUpsertModalOpen={isUpsertModalOpen}
            setIsUpsertModalOpen={setIsUpsertModalOpen}
            selectedRole={selectedRole}
            setSelectedRole={setSelectedRole}
            columns={roleColumns}
            isColumnVisible={isRoleColumnVisible}
          />
        )}

        {activeTab === 'widgets' && <WidgetAccess />}
      </div>
    </div>
  )
}

export default UserManagement
